import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleDialogOpen = () => {
    this.#refreshCart();
  };

  #handlePageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) {
      this.#refreshCart();
    }
  };

  #refreshCart() {
    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;
    if (sectionId) {
      sectionRenderer.renderSection(sectionId, { cache: false }).then(() => {
        this.#dispatchCartCountFromAPI(sectionId);
      });
    }
  }

  /**
   * Fetches cart count from Shopify Cart API and dispatches CartUpdateEvent
   * @param {string} sectionId - The section ID
   */
  async #dispatchCartCountFromAPI(sectionId) {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      
      if (!response.ok) return;
      
      const cart = await response.json();
      const itemCount = cart.item_count || 0;
      
      document.dispatchEvent(
        new CartUpdateEvent({}, sectionId, {
          itemCount,
          source: 'cart-drawer-component',
        })
      );
    } catch (_) {
      // Silently fail - cart-icon will sync from DOM as fallback
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
